import { enableGridHeaderKeyboardScroll } from '../../workflow-registry-diagnostics/workflow-grid-keyboard-scroll';
import { WorkflowEnrollmentRunInspectorComponent } from '../../workflow-registry-diagnostics/workflow-enrollment-run-inspector.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Output,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RoleService } from '@app/shared/services/roles.service';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzGridComponent } from '@template-ui/components/ez-grid/ez-grid.component';
import type { ColDef, GridOptions } from 'ag-grid-community';
import { Observable, Subscription } from 'rxjs';
import {
  mapWorkflowRegistryDiagnosticError,
  type WorkflowRegistryDiagnosticDisplayError,
} from '../../workflow-registry-diagnostics/platform-workflow-registry-diagnostics.models';
import { WorkflowDraftConfirmationService } from '../../workflow-registry-diagnostics/workflow-draft-confirmation.service';
import { calendarTimeInput } from '../../business-calendars/platform-business-calendars.models';
import { RecurringSchedulesService } from './recurring-schedules.service';
import { RecurringScheduleStateService } from './recurring-schedule-state.service';
import { ScheduleParametersComponent } from './schedule-parameters.component';
import {
  isParameterObject,
  scheduleCalendarIdentity,
  scheduleParameterErrors,
  scheduleTargetIdentity,
  type RecurringScheduleCatalog,
  type RecurringScheduleControl,
  type RecurringScheduleDefinition,
  type RecurringScheduleRun,
  type RecurringScheduleVersion,
  type ScheduleRecoveryAction,
} from './recurring-schedules.models';

@Component({
  selector: 'phms-recurring-schedules',
  standalone: true,
  imports: [
    WorkflowEnrollmentRunInspectorComponent,
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzGridComponent,
    ScheduleParametersComponent,
  ],
  templateUrl: './recurring-schedules.component.html',
  styleUrl: './recurring-schedules.component.scss',
})
export class RecurringSchedulesComponent implements OnDestroy {
  private readonly api = inject(RecurringSchedulesService);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly roles = inject(RoleService);
  private readonly state = inject(RecurringScheduleStateService);
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  readonly canRead = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.read',
  );
  readonly canAuthor = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.author',
  );
  readonly canPublish = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.publish',
  );
  @Input() workflowCode: string | null = null;
  @Input() interactionBlocked = false;
  @Output() deliveryRequested = new EventEmitter<{
    eventId: string;
    consumerId: string;
  }>();
  catalog: RecurringScheduleCatalog | null = null;
  versions: RecurringScheduleVersion[] = [];
  runs: RecurringScheduleRun[] = [];
  selected: RecurringScheduleVersion | null = null;
  definition: RecurringScheduleDefinition | null = null;
  selectedRun: RecurringScheduleRun | null = null;
  newLabel = '';
  controlVersionId: number | null = null;
  controlEnabled = false;
  existingWorkPolicy = 'drain';
  controlReason = '';
  runNowReason = '';
  recoveryReason = '';
  loading = false;
  loadingRuns = false;
  busy = false;
  accessDenied = false;
  staleDraft = false;
  staleControl = false;
  staleRun = false;
  error: WorkflowRegistryDiagnosticDisplayError | null = null;
  runsError: WorkflowRegistryDiagnosticDisplayError | null = null;
  validation = '';
  notice = '';
  private baseline = '';
  private retainedSource: {
    id: number;
    revision: number;
    eTag: string;
  } | null = null;
  private controlBaseline = JSON.stringify([null, false, 'drain']);
  private catalogGeneration = 0;
  private runsGeneration = 0;
  private catalogRequest?: Subscription;
  private runsRequest?: Subscription;
  private mutationRequest?: Subscription;
  private intent: { fingerprint: string; key: string } | null = null;
  readonly targetIdentity = scheduleTargetIdentity;
  readonly calendarIdentity = scheduleCalendarIdentity;
  readonly versionColumns: ColDef<RecurringScheduleVersion>[] = [
    {
      colId: 'label',
      headerName: 'Schedule',
      valueGetter: ({ data }) => data?.definition.label,
      minWidth: 190,
      flex: 1,
    },
    {
      colId: 'version',
      headerName: 'Version',
      valueGetter: ({ data }) => data?.definition.version,
      width: 100,
    },
    { field: 'statusCode', headerName: 'State', width: 120 },
    { field: 'revision', headerName: 'Revision', width: 100 },
  ];
  readonly versionGridOptions: GridOptions<RecurringScheduleVersion> = {
    domLayout: 'autoHeight',
    onGridReady: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onGridSizeChanged: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onFirstDataRendered: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onRowDataUpdated: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    getRowId: ({ data }) => String(data.id),
    onRowClicked: ({ data }) => {
      if (data) void this.selectVersion(data);
    },
    suppressCellFocus: false,
  };
  readonly runColumns: ColDef<RecurringScheduleRun>[] = [
    {
      field: 'runKind',
      headerName: 'Started by',
      width: 120,
      valueFormatter: ({ value }) =>
        value === 'manual' ? 'Run now' : 'Schedule',
    },
    { field: 'localDate', headerName: 'Local date', width: 135 },
    { field: 'definitionVersion', headerName: 'Version', width: 100 },
    { field: 'status', headerName: 'Scheduling state', minWidth: 140 },
    { field: 'deliveryStatus', headerName: 'Delivery state', minWidth: 130 },
    { field: 'dueUtc', headerName: 'Due (UTC)', minWidth: 200, flex: 1 },
    { field: 'attempts', headerName: 'Capture attempts', width: 155 },
    { field: 'issueCode', headerName: 'Issue', minWidth: 190, flex: 1 },
  ];
  readonly runGridOptions: GridOptions<RecurringScheduleRun> = {
    domLayout: 'autoHeight',
    onGridReady: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onGridSizeChanged: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onFirstDataRendered: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onRowDataUpdated: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    getRowId: ({ data }) => data.id,
    onRowClicked: ({ data }) => {
      if (data) this.selectRun(data);
    },
    suppressCellFocus: false,
  };
  constructor() {
    effect(() => {
      const allowed = this.canRead();
      untracked(() => {
        if (allowed) this.refresh();
        else this.clearProtectedData();
      });
    });
  }
  ngOnDestroy(): void {
    this.catalogGeneration++;
    this.runsGeneration++;
    this.catalogRequest?.unsubscribe();
    this.runsRequest?.unsubscribe();
    this.mutationRequest?.unsubscribe();
    this.state.setDirty(false);
    this.state.setOperationInProgress(false);
  }
  @HostListener('window:beforeunload', ['$event']) beforeUnload(
    event: BeforeUnloadEvent,
  ): void {
    if (this.state.isNavigationBlocked()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  get writable(): boolean {
    return (
      this.canRead() &&
      this.canAuthor() &&
      !this.accessDenied &&
      !this.busy &&
      !this.interactionBlocked
    );
  }
  get editable(): boolean {
    return (
      this.writable &&
      this.selected?.statusCode === 'draft' &&
      !this.staleDraft &&
      !this.loading
    );
  }
  get draftDirty(): boolean {
    return (
      !!this.definition && JSON.stringify(this.definition) !== this.baseline
    );
  }
  get control(): RecurringScheduleControl | null {
    return (
      this.catalog?.controls.find(
        (row) => row.code === this.selected?.definition.code,
      ) ?? null
    );
  }
  get selectedPublishedVersion(): RecurringScheduleVersion | null {
    return (
      this.versions.find(
        (row) => row.id === this.control?.publishedVersionId,
      ) ?? null
    );
  }
  get executionTarget() {
    const definition = this.selectedPublishedVersion?.definition;
    return this.catalog?.targets.find(
      (row) =>
        row.code === definition?.triggerCode &&
        row.version === definition.triggerVersion &&
        row.hash === definition.triggerHash,
    );
  }
  get publishedVersions(): RecurringScheduleVersion[] {
    return this.versions.filter(
      (row) =>
        row.definition.code === this.selected?.definition.code &&
        row.statusCode === 'published',
    );
  }
  get targetKey(): string {
    const value = this.definition;
    return value
      ? scheduleTargetIdentity({
          code: value.triggerCode,
          version: value.triggerVersion,
          hash: value.triggerHash,
        })
      : '';
  }
  get calendarKey(): string {
    const value = this.definition;
    return value
      ? scheduleCalendarIdentity({
          code: value.calendarCode,
          versionCode: value.calendarVersionCode,
          contentHash: value.calendarContentHash,
        })
      : '';
  }
  get target() {
    return (
      this.catalog?.targets.find(
        (row) => this.targetIdentity(row) === this.targetKey,
      ) ?? null
    );
  }
  get calendar() {
    return (
      this.catalog?.calendars.find(
        (row) => this.calendarIdentity(row) === this.calendarKey,
      ) ?? null
    );
  }
  get parameters(): Record<string, unknown> {
    return isParameterObject(this.definition?.parameters)
      ? this.definition.parameters
      : {};
  }
  get publicationErrors(): string[] {
    const d = this.definition;
    if (!d) return [];
    const issues: string[] = [];
    if (!this.target)
      issues.push(
        'Select an installed compatible operation and exact version.',
      );
    if (!this.calendar)
      issues.push('Select an available exact calendar version.');
    if (!d.label.trim()) issues.push('Supply a schedule name.');
    if (!dateShape.test(d.startDate)) issues.push('Supply a start date.');
    if (!d.endDate || !dateShape.test(d.endDate) || d.endDate <= d.startDate)
      issues.push('Supply an exclusive end date after the start date.');
    else {
      const limit = new Date(`${d.startDate}T00:00:00Z`);
      limit.setUTCFullYear(limit.getUTCFullYear() + 10);
      if (d.endDate > limit.toISOString().slice(0, 10))
        issues.push('The active window cannot exceed ten years.');
    }
    if (!calendarTimeInput(d.localTime))
      issues.push('Supply a valid local time.');
    if (
      !Number.isInteger(d.intervalDays) ||
      d.intervalDays < 1 ||
      d.intervalDays > 366
    )
      issues.push('Repeat every 1 to 366 days.');
    if (
      !Number.isInteger(d.catchUpLimit) ||
      d.catchUpLimit < 1 ||
      d.catchUpLimit > 32
    )
      issues.push('Catch-up limit must be 1 to 32.');
    if (
      this.calendar &&
      (d.timeZoneId !== this.calendar.timeZoneId ||
        d.startDate < this.calendar.effectiveFrom ||
        (this.calendar.effectiveTo &&
          (!d.endDate || d.endDate > this.calendar.effectiveTo)))
    )
      issues.push(
        'Use the exact calendar time zone and stay within its effective window.',
      );
    if (
      d.gapPolicy !== 'skip' ||
      !['earlier', 'later'].includes(d.ambiguousTimePolicy) ||
      !['skip', 'catch-up'].includes(d.misfirePolicy) ||
      !['allow', 'skip-while-unfinished'].includes(d.overlapPolicy) ||
      d.workflowSelectionPolicy !== 'operation-current-state' ||
      d.configurationSelectionPolicy !== 'frozen-parameters-current-authority'
    )
      issues.push('Repair the unsupported retained scheduling policy.');
    if (this.target)
      issues.push(
        ...scheduleParameterErrors(
          this.target.registration.payloadSchema.fields,
          this.parameters,
        ),
      );
    return issues;
  }
  get canPublishVersion(): boolean {
    return (
      this.canRead() &&
      !this.accessDenied &&
      !this.busy &&
      !this.interactionBlocked &&
      !this.loading &&
      !this.staleDraft &&
      this.selected?.statusCode === 'draft' &&
      this.canPublish() &&
      !this.draftDirty &&
      !this.publicationErrors.length
    );
  }
  changed(): void {
    this.validation = '';
    this.notice = '';
    this.syncDirty();
  }
  private syncDirty(): void {
    this.state.setDirty(
      this.draftDirty ||
        !!this.newLabel.trim() ||
        !!this.controlReason ||
        !!this.recoveryReason ||
        !!this.runNowReason.trim() ||
        this.controlInputIdentity() !== this.controlBaseline,
    );
  }
  private controlInputIdentity(): string {
    return JSON.stringify([
      this.controlVersionId,
      this.controlEnabled,
      this.existingWorkPolicy,
    ]);
  }
  selectTarget(key: string): void {
    if (!this.editable || !this.definition) return;
    const target = this.catalog?.targets.find(
      (row) => this.targetIdentity(row) === key,
    );
    if (target) {
      this.definition = {
        ...this.definition,
        triggerCode: target.code,
        triggerVersion: target.version,
        triggerHash: target.hash,
        ...(target.entryPoint
          ? {
              intervalDays: 1,
              workingDaysOnly: false,
              misfirePolicy: 'skip',
              overlapPolicy: 'skip-while-unfinished',
              localTime: '02:00:00',
            }
          : {}),
      };
      this.changed();
    }
  }
  selectCalendar(key: string): void {
    if (!this.editable || !this.definition) return;
    const calendar = this.catalog?.calendars.find(
      (row) => this.calendarIdentity(row) === key,
    );
    if (calendar) {
      this.definition = {
        ...this.definition,
        calendarCode: calendar.code,
        calendarVersionCode: calendar.versionCode,
        calendarContentHash: calendar.contentHash,
        timeZoneId: calendar.timeZoneId,
      };
      this.changed();
    }
  }
  setParameters(parameters: Record<string, unknown>): void {
    if (this.editable && this.definition) {
      this.definition = { ...this.definition, parameters };
      this.changed();
    }
  }
  async selectVersion(row: RecurringScheduleVersion): Promise<void> {
    if (
      !this.canRead() ||
      this.accessDenied ||
      this.busy ||
      this.interactionBlocked ||
      !this.versions.some((version) => version.id === row.id) ||
      this.selected?.id === row.id
    )
      return;
    if (!(await this.confirmDiscard())) return;
    this.installVersion(row);
    this.installControl();
    this.selectedRun = null;
    this.runs = [];
    this.recoveryReason = '';
    this.loadRuns();
    this.syncDirty();
  }
  selectVersionById(id: number): void {
    const row = this.versions.find((version) => version.id === Number(id));
    if (row) void this.selectVersion(row);
  }
  selectRunById(id: string): void {
    const row = this.runs.find((run) => run.id === id);
    if (row) this.selectRun(row);
  }
  private async confirmDiscard(): Promise<boolean> {
    if (!this.state.isDirty()) return true;
    return this.confirmation.confirm({
      heading: 'Discard schedule changes?',
      message:
        'Unsaved schedule settings and entered control or recovery reasons will be lost.',
      confirmText: 'Discard changes',
      cancelText: 'Keep editing',
    });
  }
  private installVersion(row: RecurringScheduleVersion): void {
    this.selected = row;
    this.retainedSource = {
      id: row.id,
      revision: row.revision,
      eTag: row.eTag,
    };
    this.definition = structuredClone(row.definition);
    this.baseline = JSON.stringify(this.definition);
    this.staleDraft = false;
    this.validation = '';
    this.error = null;
  }
  private installControl(): void {
    const control = this.control;
    this.controlVersionId = control?.publishedVersionId ?? null;
    this.controlEnabled = control?.enabled ?? false;
    this.existingWorkPolicy = control?.existingWorkPolicy ?? 'drain';
    this.controlReason = '';
    this.controlBaseline = this.controlInputIdentity();
    this.staleControl = false;
  }
  async reloadSelected(): Promise<void> {
    if (this.busy || this.interactionBlocked || !(await this.confirmDiscard()))
      return;
    const row = this.versions.find(
      (version) => version.id === this.selected?.id,
    );
    if (row) this.installVersion(row);
    this.installControl();
    this.recoveryReason = '';
    this.syncDirty();
    this.refresh();
  }
  refresh(): void {
    if (!this.canRead() || this.busy || this.interactionBlocked) return;
    const generation = ++this.catalogGeneration;
    this.catalogRequest?.unsubscribe();
    this.loading = true;
    this.error = null;
    this.accessDenied = false;
    this.catalogRequest = this.api.catalog().subscribe({
      next: (catalog) => {
        if (generation !== this.catalogGeneration || !this.canRead()) return;
        if (
          !catalog ||
          catalog.contractVersion !== 1 ||
          !Array.isArray(catalog.targets) ||
          !Array.isArray(catalog.calendars) ||
          !Array.isArray(catalog.controls) ||
          !Array.isArray(catalog.versions) ||
          catalog.versions.some((row) => !validVersion(row))
        ) {
          this.readFailure(new Error('Invalid recurring schedules catalog'));
          return;
        }
        const oldControl = this.control;
        const dirty = this.draftDirty;
        const targets = this.workflowCode
          ? catalog.targets.filter(
              (row) => row.entryPoint?.workflowCode === this.workflowCode,
            )
          : catalog.targets;
        const codes = new Set(targets.map((row) => row.code));
        const versions = this.workflowCode
          ? catalog.versions.filter(
              (row) =>
                codes.has(row.definition.triggerCode) ||
                row.id === this.selected?.id,
            )
          : catalog.versions;
        this.catalog = {
          ...catalog,
          targets,
          versions,
          controls: this.workflowCode
            ? catalog.controls.filter((row) =>
                versions.some(
                  (version) => version.definition.code === row.code,
                ),
              )
            : catalog.controls,
        };
        this.versions = versions;
        const latest = this.versions.find(
          (row) => row.id === (this.selected?.id ?? this.retainedSource?.id),
        );
        if (!this.selected && this.retainedSource && latest) {
          this.selected = latest;
          this.staleDraft =
            latest.eTag !== this.retainedSource.eTag ||
            latest.revision !== this.retainedSource.revision;
        }
        if (
          this.selected &&
          (!latest ||
            latest.eTag !== this.selected.eTag ||
            latest.revision !== this.selected.revision)
        ) {
          if (dirty) this.staleDraft = true;
          else if (latest) this.installVersion(latest);
          else this.staleDraft = true;
        }
        if (
          oldControl &&
          this.control &&
          (oldControl.eTag !== this.control.eTag ||
            oldControl.revision !== this.control.revision)
        )
          this.staleControl = true;
        if (
          !this.controlReason &&
          this.controlInputIdentity() === this.controlBaseline
        )
          this.installControl();
        this.loading = false;
        this.syncDirty();
        this.loadRuns();
      },
      error: (error) => {
        if (generation === this.catalogGeneration) this.readFailure(error);
      },
    });
  }
  private readFailure(error: unknown): void {
    this.loading = false;
    this.error = mapWorkflowRegistryDiagnosticError(error);
    if (forbidden(error)) this.clearProtectedData();
  }
  loadRuns(): void {
    if (
      !this.canRead() ||
      this.accessDenied ||
      this.busy ||
      this.interactionBlocked
    )
      return;
    const generation = ++this.runsGeneration;
    this.runsRequest?.unsubscribe();
    this.loadingRuns = true;
    this.runsError = null;
    const selectedCode = this.selected?.definition.code;
    if (this.workflowCode && !selectedCode) {
      this.runs = [];
      this.loadingRuns = false;
      return;
    }
    this.runsRequest = this.api.runs(selectedCode).subscribe({
      next: (rows) => {
        if (generation !== this.runsGeneration || !this.canRead()) return;
        if (
          !Array.isArray(rows) ||
          rows.length > 50 ||
          rows.some(
            (row) =>
              !validRun(row) || (selectedCode && row.code !== selectedCode),
          )
        ) {
          this.loadingRuns = false;
          this.runsError = mapWorkflowRegistryDiagnosticError(
            new Error('Invalid schedule run contract'),
          );
          return;
        }
        this.runs = rows;
        if (this.selectedRun) {
          const current = rows.find((row) => row.id === this.selectedRun?.id);
          if (!current || current.eTag !== this.selectedRun.eTag)
            this.staleRun = true;
          else this.selectedRun = current;
        }
        this.loadingRuns = false;
      },
      error: (error) => {
        if (generation !== this.runsGeneration) return;
        this.loadingRuns = false;
        this.runsError = mapWorkflowRegistryDiagnosticError(error);
        if (forbidden(error)) this.clearProtectedData();
      },
    });
  }
  create(nextVersion = false): void {
    if (!this.writable || this.loading || this.draftDirty) return;
    if (
      this.controlReason ||
      this.recoveryReason ||
      this.controlInputIdentity() !== this.controlBaseline
    ) {
      this.validation =
        'Apply or discard entered control and recovery changes before opening another draft.';
      return;
    }
    const code = nextVersion ? this.selected?.definition.code : undefined;
    const label = nextVersion
      ? (this.selected?.definition.label ?? '')
      : this.newLabel.trim();
    if (!label || label.length > 160) {
      this.validation = 'Supply a schedule name of up to 160 characters.';
      return;
    }
    if (nextVersion && !code) return;
    const key = this.writeKey(['create', label, code]);
    this.mutate(this.api.create(label, key, code), (row) => {
      if (
        !validVersion(row) ||
        row.statusCode !== 'draft' ||
        (code && row.definition.code !== code)
      )
        throw new Error('Unexpected created schedule identity');
      this.upsert(row);
      this.installVersion(row);
      if (
        this.workflowCode &&
        !nextVersion &&
        this.catalog?.targets.length === 1
      )
        this.selectTarget(this.targetIdentity(this.catalog.targets[0]));
      this.newLabel = '';
      this.installControl();
      this.runsGeneration++;
      this.runsRequest?.unsubscribe();
      this.runs = [];
      this.selectedRun = null;
      this.loadRuns();
      this.notice =
        'Draft opened. Save settings, then publish an immutable version; publication does not enable scheduling.';
      this.refresh();
    });
  }
  save(): void {
    if (
      !this.editable ||
      !this.selected ||
      !this.definition ||
      !this.draftDirty
    )
      return;
    const source = this.selected,
      definition = structuredClone(this.definition);
    const time = calendarTimeInput(definition.localTime);
    if (
      !time ||
      !dateShape.test(definition.startDate) ||
      (definition.endDate !== null && !dateShape.test(definition.endDate)) ||
      !Number.isInteger(definition.intervalDays) ||
      !Number.isInteger(definition.catchUpLimit)
    ) {
      this.validation =
        'Supply valid date, local time and whole-number cadence fields before saving. Operation, parameters and end date may remain incomplete.';
      return;
    }
    definition.localTime = time;
    this.mutate(
      this.api.save(
        source,
        definition,
        this.writeKey(['save', source.id, source.eTag, definition]),
      ),
      (row) => {
        assertVersionAdvance(source, row, 'draft');
        this.upsert(row);
        this.installVersion(row);
        this.notice =
          'Draft settings saved. Publication and scheduling remain separate actions.';
      },
      'draft',
    );
  }
  publish(): void {
    if (!this.canPublishVersion || !this.selected) {
      this.validation = this.draftDirty
        ? 'Save draft changes before publishing.'
        : this.publicationErrors.join(' ');
      return;
    }
    const source = this.selected;
    this.mutate(
      this.api.publish(
        source,
        this.writeKey(['publish', source.id, source.eTag]),
      ),
      (row) => {
        assertVersionAdvance(source, row, 'published');
        this.upsert(row);
        this.installVersion(row);
        this.notice =
          'Version published. Select it explicitly in scheduling control to enroll future work. Existing selected versions and run snapshots are unchanged.';
        this.refresh();
      },
      'draft',
    );
  }
  changeControl(): void {
    const source = this.control;
    if (!this.writable || !source || this.loading || this.staleControl) return;
    if (
      !this.controlVersionId ||
      !this.publishedVersions.some((row) => row.id === this.controlVersionId) ||
      !['drain', 'cancel-pending'].includes(this.existingWorkPolicy) ||
      !this.controlReason.trim()
    ) {
      this.validation =
        'Select a published version, existing-work policy and a control reason.';
      return;
    }
    const command = {
      expectedRevision: source.revision,
      publishedVersionId: this.controlVersionId,
      enabled: this.controlEnabled,
      existingWorkPolicy: this.existingWorkPolicy,
      reason: this.controlReason.trim(),
    };
    this.mutate(
      this.api.control(
        source,
        command,
        this.writeKey(['control', source.code, source.eTag, command]),
      ),
      (control) => {
        if (
          control.code !== source.code ||
          control.revision <= source.revision ||
          !control.eTag ||
          control.eTag === source.eTag ||
          control.publishedVersionId !== command.publishedVersionId ||
          control.enabled !== command.enabled
        )
          throw new Error('Unexpected schedule control receipt');
        this.catalog = {
          ...this.catalog!,
          controls: this.catalog!.controls.map((row) =>
            row.code === control.code ? control : row,
          ),
        };
        this.installControl();
        this.notice = control.enabled
          ? 'Scheduling control enabled. The installation producer setting still governs execution.'
          : 'Scheduling control disabled. Committed invocations keep their existing delivery and recovery lifecycle.';
        this.loadRuns();
      },
      'control',
    );
  }
  get canRunNow(): boolean {
    return (
      this.writable &&
      !this.loading &&
      !!this.control?.publishedVersionId &&
      !!this.catalog?.producerEnabled &&
      !this.staleControl &&
      !this.draftDirty &&
      !this.controlReason.trim() &&
      this.controlInputIdentity() === this.controlBaseline &&
      (!this.executionTarget?.entryPoint ||
        this.executionTarget.entryPoint.workerEnabled) &&
      !this.runs.some(
        (run) =>
          ['pending', 'leased', 'blocked'].includes(run.status) ||
          (run.status === 'captured' && run.deliveryStatus !== 'completed'),
      )
    );
  }
  runNow(): void {
    const control = this.control;
    if (!this.canRunNow || !control || !this.runNowReason.trim()) return;
    const reason = this.runNowReason.trim();
    this.mutate(
      this.api.runNow(
        control,
        reason,
        this.writeKey(['run-now', control.code, control.eTag, reason]),
      ),
      (row) => {
        if (
          !row ||
          row.code !== control.code ||
          row.runKind !== 'manual' ||
          row.status !== 'pending' ||
          row.definitionVersion !==
            this.versions.find(
              (version) => version.id === control.publishedVersionId,
            )?.definition.version
        )
          throw new Error('Unexpected immediate-run receipt');
        this.runs = [row, ...this.runs.filter((run) => run.id !== row.id)];
        this.selectedRun = row;
        this.runNowReason = '';
        this.notice =
          'Run requested using the selected published schedule. Refresh runs for progress. The next scheduled run is unchanged.';
      },
      'control',
    );
  }
  selectRun(row: RecurringScheduleRun): void {
    if (
      !this.canRead() ||
      this.accessDenied ||
      this.busy ||
      !this.runs.some((run) => run.id === row.id)
    )
      return;
    if (this.selectedRun?.id !== row.id && this.recoveryReason.trim()) {
      this.validation =
        'Clear or use the entered recovery reason before selecting another run.';
      return;
    }
    this.selectedRun = row;
    this.staleRun = false;
    this.validation = '';
    this.syncDirty();
  }
  recover(action: ScheduleRecoveryAction): void {
    const source = this.selectedRun;
    if (
      !this.writable ||
      !source ||
      this.staleRun ||
      this.loadingRuns ||
      !source.recoveryActions.includes(action)
    )
      return;
    if (!this.recoveryReason.trim()) {
      this.validation = 'Supply a reason for this recovery action.';
      return;
    }
    const reason = this.recoveryReason.trim();
    this.mutate(
      this.api.recover(
        source,
        action,
        reason,
        this.writeKey(['recover', source.id, source.eTag, action, reason]),
      ),
      (row) => {
        if (
          !validRun(row) ||
          (action === 'retry'
            ? row.id !== source.id || row.eTag === source.eTag
            : row.id === source.id || row.rerunOf !== source.id)
        )
          throw new Error('Unexpected schedule recovery receipt');
        this.runs = [
          row,
          ...this.runs.filter((run) => run.id !== row.id),
        ].slice(0, 50);
        this.selectedRun = row;
        this.recoveryReason = '';
        this.staleRun = false;
        this.notice =
          action === 'retry'
            ? 'The same scheduling occurrence is ready for another capture attempt.'
            : 'A distinct rerun was created for current eligibility evaluation. The prior run remains unchanged.';
      },
      'run',
    );
  }
  openDelivery(): void {
    const run = this.selectedRun;
    if (
      this.canRead() &&
      !this.accessDenied &&
      !this.busy &&
      run?.invocationId &&
      run.deliveryConsumerId
    )
      this.deliveryRequested.emit({
        eventId: run.invocationId,
        consumerId: run.deliveryConsumerId,
      });
  }
  private upsert(row: RecurringScheduleVersion): void {
    this.versions = [
      row,
      ...this.versions.filter((version) => version.id !== row.id),
    ];
    if (this.catalog)
      this.catalog = { ...this.catalog, versions: this.versions };
  }
  private writeKey(parts: unknown[]): string {
    const fingerprint = JSON.stringify(parts);
    if (this.intent?.fingerprint !== fingerprint)
      this.intent = { fingerprint, key: crypto.randomUUID() };
    return this.intent.key;
  }
  private mutate<T>(
    request: Observable<T>,
    accept: (result: T) => void,
    slot?: 'draft' | 'control' | 'run',
  ): void {
    // A pre-write read must never rewind a later accepted exact-source receipt.
    this.catalogGeneration++;
    this.runsGeneration++;
    this.catalogRequest?.unsubscribe();
    this.runsRequest?.unsubscribe();
    this.loading = false;
    this.loadingRuns = false;
    this.busy = true;
    this.state.setOperationInProgress(true);
    this.validation = '';
    this.error = null;
    this.notice = '';
    this.mutationRequest = request.subscribe({
      next: (result) => {
        this.busy = false;
        this.state.setOperationInProgress(false);
        try {
          if (!this.canRead() || this.accessDenied) return;
          accept(result);
          this.intent = null;
        } catch (error) {
          this.error = mapWorkflowRegistryDiagnosticError(error);
          this.markStale(slot);
        }
        this.syncDirty();
      },
      error: (error) => {
        this.busy = false;
        this.state.setOperationInProgress(false);
        this.error = mapWorkflowRegistryDiagnosticError(error);
        if ((error as { status?: number })?.status === 412)
          this.markStale(slot);
        if (forbidden(error)) this.clearProtectedData();
        this.syncDirty();
      },
    });
  }
  private markStale(slot?: string): void {
    if (slot === 'draft') this.staleDraft = true;
    if (slot === 'control') this.staleControl = true;
    if (slot === 'run') this.staleRun = true;
  }
  private clearProtectedData(): void {
    this.catalogGeneration++;
    this.runsGeneration++;
    this.catalogRequest?.unsubscribe();
    this.runsRequest?.unsubscribe();
    this.catalog = null;
    this.versions = [];
    this.runs = [];
    this.selectedRun = null;
    this.selected = null;
    this.accessDenied = true;
    this.staleDraft = !!this.definition;
    this.loading = false;
    this.loadingRuns = false;
    this.syncDirty();
  }
}
const dateShape = /^\d{4}-\d{2}-\d{2}$/;
function forbidden(error: unknown): boolean {
  return [401, 403].includes((error as { status?: number })?.status ?? 0);
}
function validVersion(row: RecurringScheduleVersion): boolean {
  return (
    !!row &&
    Number.isSafeInteger(row.id) &&
    row.id > 0 &&
    Number.isSafeInteger(row.revision) &&
    row.revision > 0 &&
    !!row.eTag &&
    ['draft', 'published'].includes(row.statusCode) &&
    !!row.definition?.code &&
    Number.isSafeInteger(row.definition.version) &&
    row.definition.version > 0 &&
    typeof row.definition.label === 'string'
  );
}
function assertVersionAdvance(
  source: RecurringScheduleVersion,
  row: RecurringScheduleVersion,
  status: string,
): void {
  if (
    !validVersion(row) ||
    row.id !== source.id ||
    row.definition.code !== source.definition.code ||
    row.definition.version !== source.definition.version ||
    row.revision <= source.revision ||
    row.eTag === source.eTag ||
    row.statusCode !== status
  )
    throw new Error('Unexpected exact schedule version receipt');
}
function validRun(row: RecurringScheduleRun): boolean {
  return (
    !!row &&
    !!row.id &&
    !!row.eTag &&
    Array.isArray(row.recoveryActions) &&
    row.recoveryActions.every((action) =>
      ['retry', 'rerun'].includes(action),
    ) &&
    Number.isInteger(row.attempts) &&
    row.attempts >= 0
  );
}
